import { useLocation } from "@solidjs/router";
import { PaperclipIcon } from "@storytell/bsky-icons";
import type { Component } from "solid-js";
import { Portal } from "solid-js/web";
import { useI18n } from "~/i18n/context";
import { useFileDropHandlers } from "~/lib/file-upload";
import { useWire } from "~/wire";

/**
 * Full-screen dropzone overlay that appears when files are dragged over the page
 */
export const FullScreenDropzone: Component = () => {
  const wire = useWire();
  const { t } = useI18n();

  const location = useLocation();
  // Open knowledge pane after upload if we're on a project detail page
  const isProjectDetailPage = () => location.pathname.startsWith("/projects/project_");
  const dropHandlers = useFileDropHandlers({
    canUploadAssets: () => wire.grants.canCreate("cp.assets"),
    openKnowledgePaneAfterUpload: isProjectDetailPage(),
  });

  // Override onDragOver to also stop propagation for the dropzone overlay
  const handleDragOver = (e: DragEvent) => {
    dropHandlers.onDragOver(e);
    e.stopPropagation();
  };

  return (
    <Portal mount={document.body}>
      <div
        class="pointer-events-auto fixed inset-0 z-200"
        style={{
          background: "rgba(0, 0, 0, 0.6)",
          "backdrop-filter": "blur(4px)",
        }}
        onDrop={dropHandlers.onDrop}
        onDragOver={handleDragOver}
      >
        <div class="pointer-events-auto absolute inset-0 flex items-center justify-center">
          <div
            class="pointer-events-auto mx-auto w-full max-w-2xl rounded-3xl border-4 border-dashed p-12"
            style={{
              "border-color": "rgba(var(--color-primary-rgb, 99, 102, 241), 0.5)",
              background: "linear-gradient(135deg, rgba(0,0,0,0.3), rgba(0,0,0,0.1))",
              "box-shadow": `
              inset 0 6px 20px rgba(0,0,0,0.4),
              inset 0 3px 12px rgba(0,0,0,0.3),
              0 0 60px rgba(var(--color-primary-rgb, 99, 102, 241), 0.2)
            `,
            }}
          >
            <div class="text-center">
              <div
                class="mb-4 inline-flex size-20 items-center justify-center rounded-full"
                style={{
                  background: "linear-gradient(135deg, rgba(0,0,0,0.3), rgba(0,0,0,0.1))",
                  "box-shadow": "inset 0 2px 8px rgba(0,0,0,0.4), inset 0 1px 4px rgba(0,0,0,0.3)",
                }}
              >
                <PaperclipIcon class="size-10 text-brand/80" />
              </div>
              <p class="mb-2 text-2xl font-medium text-white/90">{t("dropzone_drop_files_here")}</p>
              <p class="text-sm text-white/60">{t("dropzone_upload_files_description")}</p>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};
